"use client";

import { Badge } from "@velpha/ui/components/badge";
import { cn } from "@velpha/ui/lib/utils";
import {
  type RiskExample,
  type RiskLevel,
  COLOR,
  seriesToPoints,
  pointsToLinePath,
  pointsToAreaPath,
  Dot,
} from "./_shared";
import { Card } from "@velpha/ui/components/card";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

// ── Risk level chart ────────────────────────────────────────────────

const STRINGS: Record<Locale, { label: Record<RiskLevel, string>; description: Record<RiskLevel, string> }> = {
  ja: {
    label: { low: "低リスク", medium: "中リスク", high: "高リスク" },
    description: {
      low: "上下はあるものの、保有中の負担は比較的小さい。",
      medium: "途中で大きめの値動きがあり、人によっては不安を感じやすい。",
      high: "大きく増える可能性がある一方で、大きく下がることもあり、許容しにくい値動きになりやすい。",
    },
  },
  en: {
    label: { low: "Low risk", medium: "Medium risk", high: "High risk" },
    description: {
      low: "It moves up and down, but holding it is relatively undemanding.",
      medium: "There are larger swings along the way, which some people find unsettling.",
      high: "It can rise sharply, but it can also fall sharply, so the swings are often hard to tolerate.",
    },
  },
};

const RISK_COLOR: Record<RiskLevel, string> = {
  low: COLOR.emerald,
  medium: COLOR.amber,
  high: COLOR.rose,
};

const RISK_BADGE_STYLE: Record<RiskLevel, string> = {
  low: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  medium: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400",
  high: "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-400",
};

const RISK_CHART = { w: 240, h: 120, px: 10, py: 10, min: 30, max: 90 } as const;

// 説明文は locale 辞書から引くので、ここは水準と系列だけ持つ。
const RISK_EXAMPLES: Omit<RiskExample, "description">[] = [
  { level: "low", series: [50, 51, 52, 51, 53, 54, 55, 54, 56, 60] },
  { level: "medium", series: [50, 58, 49, 57, 52, 61, 55, 64, 57, 60] },
  { level: "high", series: [50, 70, 38, 76, 42, 82, 35, 86, 44, 60] },
];

function RiskLevelChart({ level, series }: Omit<RiskExample, "description">) {
  const t = STRINGS[useDocLocale()];
  const color = RISK_COLOR[level];
  const pts = seriesToPoints(series, RISK_CHART);
  const first = pts[0];
  const last = pts[pts.length - 1];
  const linePath = pointsToLinePath(pts);
  const areaPath = pointsToAreaPath(pts, RISK_CHART.h - RISK_CHART.py);
  const gradientId = `risk-grad-${level}`;

  return (
    <div className="p-4">
      <div className="flex items-center justify-between">
        <Badge size="xs" className={cn(RISK_BADGE_STYLE[level])}>
          {t.label[level]}
        </Badge>
      </div>
      <div className="mt-3 rounded-xl border border-border/60 bg-background/80 p-2">
        <svg viewBox={`0 0 ${RISK_CHART.w} ${RISK_CHART.h}`} className="h-40 w-full">
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.25" />
              <stop offset="100%" stopColor={color} stopOpacity="0.04" />
            </linearGradient>
          </defs>
          <line
            x1={RISK_CHART.px}
            y1={40}
            x2={RISK_CHART.w - RISK_CHART.px}
            y2={40}
            stroke="currentColor"
            strokeDasharray="3 6"
            opacity="0.14"
          />
          <line
            x1={RISK_CHART.px}
            y1={first.y}
            x2={RISK_CHART.w - RISK_CHART.px}
            y2={first.y}
            stroke="currentColor"
            strokeDasharray="3 6"
            opacity="0.25"
          />
          <line
            x1={RISK_CHART.px}
            y1={100}
            x2={RISK_CHART.w - RISK_CHART.px}
            y2={100}
            stroke="currentColor"
            strokeDasharray="3 6"
            opacity="0.14"
          />
          <path d={areaPath} fill={`url(#${gradientId})`} />
          <path
            d={linePath}
            fill="none"
            stroke={color}
            strokeWidth="2.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          <Dot x={first.x} y={first.y} color={color} />
          <Dot x={last.x} y={last.y} color={color} />
        </svg>
      </div>
      <div className="mt-3 text-xs leading-5 text-muted-foreground">{t.description[level]}</div>
    </div>
  );
}

// ── Export ───────────────────────────────────────────────────────────

export function DocRiskLevelChartGrid() {
  return (
    <Card className="mt-5 grid gap-3 md:grid-cols-3 p-3">
      {RISK_EXAMPLES.map((ex) => (
        <RiskLevelChart key={ex.level} {...ex} />
      ))}
    </Card>
  );
}
